import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, ExternalLink, Activity, Mountain, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import ShareButton from "@/components/ShareButton";
import TrailReports from "@/components/TrailReports";
import ShopPromoBanner from "@/components/ShopPromoBanner";
import SEO from "@/components/SEO";
import { MataBadge } from "@/components/MataMark";
import DecorationLayer from "@/components/DecorationLayer";
import MapFragment, { ElevationProfile } from "@/components/MapFragment";
import { SectionMark } from "@/components/PageHeader";
import RiderQuotes from "@/components/RiderQuotes";
import WetTrailsAlert from "@/components/WetTrailsAlert";
import { THEMES, trailStatus, fmtDateTime, LABELS } from "@/components/trailCondition";
import { ARGENTARIO_COORDS } from "@/components/EditorialLabel";
import { TES_STAGES, StageBadge } from "@/components/NextRace";

const DIFF_TEXT = { blue: "text-sky-400", red: "text-abnormal-red", black: "text-abnormal-paper" };

const STATE_THEME = THEMES;

const tr_ = (t, key, fallback) => { const v = t(key); return !v || v === key || v.startsWith("trailMeta.") ? fallback : v; };

function ytEmbed(url) {
    if (!url) return null;
    if (url.includes("/embed/")) return url;
    try {
        const u = new URL(url);
        const id = u.searchParams.get("v") || u.pathname.split("/").pop();
        return `https://www.youtube.com/embed/${id}`;
    } catch { return null; }
}

export default function TrailDetail() {
    const { t, lang } = useLang();
    const { slug } = useParams();
    const [trail, setTrail] = useState(null);
    const [all, setAll] = useState([]);
    const [combos, setCombos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/trails/${slug}`).then((r) => setTrail(r.data)).catch(() => setTrail(null)).finally(() => setLoading(false));
        api.get("/combos").then((r) => setCombos(r.data)).catch(() => setCombos([]));
        api.get("/trails").then((r) => setAll(r.data || [])).catch(() => {});
    }, [slug]);

    if (loading) return <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 label-mono text-abnormal-paperDim">{t("trailDetail.loading")}</div>;
    if (!trail) return (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-28">
            <p className="font-display text-5xl uppercase">{t("trailDetail.notFound")}</p>
            <Link to="/trail" className="text-abnormal-acid uppercase tracking-wider mt-4 inline-block">{t("trailDetail.notFoundBack")}</Link>
        </div>
    );

    const embed = ytEmbed(trail.youtube_url);
    const parentCombo = combos.find((c) => c.trail_slugs.includes(trail.slug));
    const comboName = parentCombo ? (lang === "en" && parentCombo.name_en ? parentCombo.name_en : parentCombo.name) : "";
    const idx = all.findIndex((x) => x.slug === trail.slug);
    const sheet = idx >= 0 ? String(idx + 1).padStart(2, "0") : "—";
    const total = all.length ? String(all.length).padStart(2, "0") : "—";

    const trailName = (lang === "en" && trail.name_en) ? trail.name_en : trail.name;
    const quotes = Array.isArray(trail.rider_quotes) ? trail.rider_quotes : [];
    const diffLabel = tr_(t, `trailMeta.difficulty.${trail.difficulty}`, trail.difficulty);

    const status = trailStatus(trail, lang);
    const st = status.key;
    const theme = STATE_THEME[st] || STATE_THEME.unknown;
    const stateLabel = status.label;
    const statusWhen = fmtDateTime(status.updatedAt, lang);
    const note = status.note || (st === "unknown" ? t("trails.stateUnverifiedNote") : "");
    const en = lang === "en";

    const stats = [
        { label: t("trailDetail.difficulty"), value: diffLabel, cls: DIFF_TEXT[trail.difficulty], sub: "Trailforks" },
        { label: t("trailDetail.length"), value: trail.length_km, unit: "km" },
        { label: t("trailDetail.elevation"), value: trail.elevation_m, unit: "m" },
    ];

    return (
        <div data-testid="trail-detail-page" className="bg-abnormal-ink relative">
            <SEO
                title={trailName}
                description={(quotes.find((q) => q.lang === lang) || quotes[0])?.text || (en
                    ? `MTB Enduro trail on Monte Argentario. ${trail.length_km} km · D− ${trail.elevation_m} m.`
                    : `Trail MTB Enduro a Monte Argentario. ${trail.length_km} km · D− ${trail.elevation_m} m.`)}
                image={absolutizeUrl(trail.image_url)}
                path={`/trail/${trail.slug}`}
                type="article"
            />
            <DecorationLayer page={`trail-${trail.slug}`} />

            {/* ═══ HERO — big photo, magazine opener ═══ */}
            <section className="relative min-h-[78vh] w-full overflow-hidden">
                <img src={absolutizeUrl(trail.image_url)} alt={trailName} className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/15 to-black" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

                <div className="absolute top-6 left-5 md:left-10 right-5 md:right-10 flex items-start justify-between label-mono text-abnormal-paper/85">
                    <div className="flex items-start gap-3">
                        <MataBadge size={40} testid="trail-mata-badge" className="mt-0.5 drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" />
                        <div>
                            <p><span className="text-abnormal-acid">SHEET N° {sheet}</span> / {total}</p>
                            <p className="mt-1 text-abnormal-paperDim/70">{ARGENTARIO_COORDS}</p>
                            <p className="mt-1 text-abnormal-paperDim/70 hidden sm:block">M.A.T.A. · Monte Argentario Trail Area</p>
                        </div>
                    </div>
                    <p data-testid="trail-state-banner" className={`inline-flex items-center gap-2 border ${theme.border} ${theme.text} bg-black/60 px-2.5 py-1.5`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${theme.dot} animate-pulse`} />
                        {stateLabel}
                    </p>
                </div>
                {trail.image_credit && (
                    <a href={trail.image_source || "#"} target="_blank" rel="noreferrer" data-testid="trail-image-credit"
                        className="absolute bottom-2 right-3 z-20 label-mono !text-[9px] text-abnormal-paperDim/60 hover:text-abnormal-paper bg-black/50 px-1.5 py-0.5">
                        {trail.image_credit}
                    </a>
                )}

                <div className="relative z-10 min-h-[78vh] max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-10 md:pb-14">
                    <Link to="/trail" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-2 mb-6 poster-in" data-testid="back-to-trails">
                        <ArrowLeft size={13} /> {t("trailDetail.back")}
                    </Link>
                    <p className="flex items-center gap-2 font-display uppercase text-abnormal-paper text-lg md:text-2xl leading-none tracking-tight poster-in poster-delay-1">
                        <span className={DIFF_TEXT[trail.difficulty] || "text-abnormal-acid"}>{diffLabel}</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid inline-block" />
                        <span className="text-abnormal-paperDim">{trail.length_km} km · D− {trail.elevation_m} m</span>
                    </p>
                    <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                        <h1 className="font-display uppercase leading-[0.82] tracking-tighter text-abnormal-paper text-[64px] sm:text-[96px] md:text-[140px] lg:text-[170px] poster-in poster-delay-2">
                            {trailName}<span className="text-abnormal-acid">.</span>
                        </h1>
                        <div className="poster-in poster-delay-3 pb-3"><ShareButton trail={trail} /></div>
                    </div>
                    {quotes[0] && (
                        <p data-testid="trail-hero-quote" className="mt-4 max-w-2xl font-display uppercase text-2xl md:text-3xl leading-tight text-abnormal-paperDim">
                            <span className="text-abnormal-acid">“</span>{quotes[0].text}<span className="text-abnormal-acid">”</span>
                        </p>
                    )}
                    {note && <p data-testid="trail-state-note" className={`mt-3 text-sm ${theme.text} border-l-2 ${theme.border} pl-3`}>{note}</p>}
                    {(statusWhen || status.source) && (
                        <p data-testid="trail-state-meta" className="mt-2 label-mono text-abnormal-paperDim/60">
                            {statusWhen && <>{t("trails.lastUpdated")}: <span className="text-abnormal-paperDim">{statusWhen}</span></>}
                            {status.source && <> · {status.source}</>}
                        </p>
                    )}
                    <p className="mt-2 max-w-xl text-[11px] leading-snug text-abnormal-paperDim bg-black/50 px-2 py-1 inline-block">{LABELS[en ? "en" : "it"].disclaimer}</p>
                    {TES_STAGES[trail.slug] && (
                        <Link to="/eventi#tes" data-testid={`stage-link-${trail.slug}`}
                            className="mt-5 inline-flex items-center gap-3 self-start border border-abnormal-magenta/60 bg-black/60 pl-1 pr-3 py-1 hover:border-abnormal-magenta transition-colors">
                            <StageBadge slug={trail.slug} size="lg" />
                            <span className="label-mono text-abnormal-paper">{en ? "Special stage · Toscano Enduro Series · 4 Oct 2026 →" : "Prova speciale · Toscano Enduro Series · 4 ott 2026 →"}</span>
                        </Link>
                    )}
                    {parentCombo && (
                        <Link to="/trail#combo-section" data-testid={`combo-badge-link-${parentCombo.slug}`}
                            className="mt-3 inline-flex items-center gap-2 self-start label-mono border border-abnormal-acid text-abnormal-acid px-3 py-2 hover:bg-abnormal-acid hover:text-black transition-colors">
                            <span className="bg-abnormal-acid text-black px-1.5 py-0.5 !text-[9px]">COMBO</span>
                            {t("combos.partOfCombo")}: {comboName} →
                        </Link>
                    )}
                </div>
            </section>

            {/* ═══ DOSSIER STRIP — tech data + actions, mobile-first ═══ */}
            <section data-testid="trail-dossier" className="border-y border-white/10 bg-abnormal-charcoal">
                <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                    {stats.map((s, i) => (
                        <div key={s.label} className={`px-5 py-5 border-white/10 ${i > 0 ? "border-l" : ""} md:border-l ${i === 0 ? "md:border-l-0" : ""} ${i === 2 ? "col-span-2 md:col-span-1 border-t md:border-t-0 !border-l-0 md:!border-l" : ""}`}>
                            <p className="label-mono text-abnormal-paperDim/60">{s.label}</p>
                            <p className={`font-display text-3xl md:text-4xl uppercase leading-none mt-1 ${s.cls || "text-abnormal-paper"}`}>
                                {s.value}{s.unit && <span className="text-abnormal-acid text-base ml-1">{s.unit}</span>}
                                {s.sub && <span className="label-mono text-abnormal-paperDim/50 ml-2 !text-[9px] align-middle">{s.sub}</span>}
                            </p>
                        </div>
                    ))}
                    <div className="col-span-2 px-5 py-4 border-t md:col-span-3 lg:col-span-2 lg:border-t-0 lg:border-l border-white/10 flex flex-wrap items-center gap-2" data-testid="trail-quick-maps">
                        {trail.komoot_url && (
                            <a href={trail.komoot_url} target="_blank" rel="noreferrer" data-testid="trail-komoot-link" className="btn-ghost !py-2.5 !px-3 !text-[11px]">
                                <Mountain size={12} /> Komoot <ExternalLink size={11} />
                            </a>
                        )}
                        {trail.google_maps_url && (
                            <a href={trail.google_maps_url} target="_blank" rel="noreferrer" data-testid="trail-maps-link" className="btn-ghost !py-2.5 !px-3 !text-[11px]">
                                <MapPin size={12} /> Maps <ExternalLink size={11} />
                            </a>
                        )}
                        {trail.trailforks_url && (
                            <a href={trail.trailforks_url} target="_blank" rel="noreferrer" data-testid="trail-trailforks-link" className="btn-primary !py-2.5 !px-3 !text-[11px]">
                                {trail.trailforks_url.includes("/region/") ? (en ? "Area map · Trailforks" : "Mappa dell'area · Trailforks") : (en ? "Map · GPX · Trailforks" : "Mappa · GPX · Trailforks")} <ArrowUpRight size={12} />
                            </a>
                        )}
                        {trail.trailforks_url && !trail.trailforks_url.includes("/region/") && (
                            <span className="w-full label-mono text-abnormal-paperDim/50 !text-[9px]" data-testid="trail-gpx-note">{en ? "GPX download requires a free Trailforks account" : "Il download GPX richiede un account Trailforks gratuito"}</span>
                        )}
                        {!trail.komoot_url && !trail.google_maps_url && !trail.trailforks_url && (
                            <span className="label-mono text-abnormal-paperDim/50">{t("trailDetail.openOnMap")} — n/a</span>
                        )}
                    </div>
                </div>
            </section>

            {/* ═══ BODY — editorial grid ═══ */}
            <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14">
                <div className="lg:col-span-7 space-y-12 min-w-0">
                    <WetTrailsAlert compact />
                    <RiderQuotes quotes={quotes} trailName={trailName} trailforksUrl={trail.trailforks_url} n="01" />

                    {(trail.note_fondo || trail.note_fondo_en) && (
                        <div data-testid="trail-note-fondo">
                            <SectionMark n="02" title={t("trailDetail.noteFondo")} right={<Activity size={13} className="text-abnormal-acid" />} />
                            <p className="text-abnormal-paperDim leading-relaxed whitespace-pre-line bg-abnormal-charcoal border border-white/10 p-5">
                                {(en && trail.note_fondo_en) ? trail.note_fondo_en : trail.note_fondo}
                            </p>
                        </div>
                    )}

                    {embed && (
                        <div>
                            <SectionMark n="03" title={t("trailDetail.video")} />
                            <div className="aspect-video border border-white/10 bg-black">
                                <iframe src={embed} title={trailName} data-testid="trail-youtube-embed"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen className="w-full h-full" loading="lazy" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Field map card */}
                <aside className="lg:col-span-5 space-y-6 lg:sticky lg:top-24 self-start">
                    <div data-testid="trail-field-map" className="border border-white/15 bg-abnormal-charcoal p-4 md:p-5 topo-lines">
                        <div className="flex items-center justify-between label-mono text-abnormal-paperDim/70">
                            <span><span className="text-abnormal-acid">ABN. FIELD MAP</span> · SHEET N° {sheet}</span>
                            <span>{ARGENTARIO_COORDS}</span>
                        </div>
                        <div className="mt-3 border border-white/10 bg-black/40 aspect-[16/10] overflow-hidden">
                            <MapFragment seed={trail.slug} className="w-full h-full" animate />
                        </div>
                        <div className="mt-3 grid grid-cols-[1fr_auto] gap-3 items-end">
                            <div className="border border-white/10 h-14 overflow-hidden">
                                <ElevationProfile seed={trail.slug} className="w-full h-full" />
                            </div>
                            <div className="text-right">
                                <p className="label-mono text-abnormal-paperDim/60">{en ? "Drop" : "Dislivello"}</p>
                                <p className="font-display text-3xl leading-none text-abnormal-paper">-{trail.elevation_m}<span className="text-abnormal-acid text-sm ml-0.5">m</span></p>
                            </div>
                        </div>
                        <p className="mt-3 label-mono text-abnormal-paperDim/50">
                            // {trail.length_km} km <span className="text-abnormal-acid">·</span> {diffLabel} <span className="text-abnormal-acid">·</span> {en ? "schematic" : "schema indicativo"}
                        </p>
                    </div>

                    <div className="border-2 border-abnormal-red p-5">
                        <p className="label-mono text-abnormal-red">{t("trailDetail.safetyTitle")}</p>
                        <p className="text-sm text-abnormal-paperDim mt-2 leading-relaxed">{t("trailDetail.safetyText")}</p>
                    </div>
                </aside>
            </section>

            <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-10">
                <TrailReports trailSlug={trail.slug} trailName={trailName} />
            </section>

            <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20">
                <ShopPromoBanner testidPrefix="trail-shop-promo" />
            </section>
        </div>
    );
}
